/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * DobPromptModal — retrospective DOB capture for legacy accounts.
 *
 * Shown when the login response returns `requiresDob: true`. Dismissible
 * today (soft gate) unless the server-side hard-gate date has passed.
 * Submits to PATCH /api/auth/dob and refreshes the stored user object.
 */

import React, { useState } from 'react';
import { Calendar, X, AlertCircle } from 'lucide-react';
import api from '../../services/api';

const todayIso = () => new Date().toISOString().slice(0, 10);

const DobPromptModal = ({ open, onClose, onSaved, dismissible = true }) => {
  const [dob, setDob] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!dob) {
      setError('Please enter your date of birth.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.request('/auth/dob', {
        method: 'PATCH',
        body: JSON.stringify({ dateOfBirth: dob })
      });
      if (res && res.success) {
        // Refresh the cached user object so the guard-driven flows see the new status.
        try {
          const cached = JSON.parse(localStorage.getItem('user') || '{}');
          const merged = {
            ...cached,
            ageStatus: res.data?.ageStatus || 'adult',
            requiresDob: false
          };
          localStorage.setItem('user', JSON.stringify(merged));
        } catch (_e) { /* non-fatal */ }
        if (onSaved) onSaved(res.data);
        if (onClose) onClose();
      } else {
        setError(res?.error || 'Could not save your date of birth.');
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Could not save your date of birth.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="relative bg-white rounded-brandCard shadow-brand border border-brand-border p-6 w-full max-w-md">
        {dismissible && (
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 text-brand-muted hover:text-brand-text"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-5 h-5 text-brand-cyan" />
          <h2 className="text-lg font-semibold text-brand-text">Confirm your age</h2>
        </div>
        <p className="text-sm text-brand-muted mb-4">
          SkillDelivery is for users 18 and over. Please add your date of birth so we
          can enable community matching and personalised features for your account.
        </p>

        {error && (
          <div className="mb-3 p-3 rounded-brand bg-red-50 border border-red-200 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            max={todayIso()}
            className="w-full px-4 py-2 border border-brand-border rounded-brand focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:border-brand-cyan"
            required
          />
          <div className="flex gap-2 justify-end">
            {dismissible && (
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-brand-muted hover:text-brand-text"
                disabled={submitting}
              >
                Later
              </button>
            )}
            <button
              type="submit"
              disabled={submitting || !dob}
              className="px-4 py-2 rounded-brand bg-gradient-to-br from-brand-cyanLight to-brand-blue text-white text-sm font-semibold shadow-brandButton hover:shadow-brandButtonHover disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DobPromptModal;
